import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { darkTheme, lightTheme } from "./themes";

const API = `${window.location.protocol}//${window.location.hostname}/api`;
const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
});

// ─── CONTEXT ────────────────────────────────────────────────────────────────
const ThemeContext = createContext(null);

// ─── PROVIDER ───────────────────────────────────────────────────────────────
export function ThemeProvider({ children }) {
    // localStorage'dan başlangıç değeri — backend yanıt gelene kadar flash önler
    const [themeName, setThemeName] = useState(
        () => localStorage.getItem("sf_theme") || "dark"
    );
    const [syncing, setSyncing] = useState(false);

    const t = themeName === "light" ? lightTheme : darkTheme;

    // ── Backend'den kullanıcı tercihini çek (login sonrası) ──────────────────
    const fetchPreference = useCallback(async () => {
        const token = localStorage.getItem("token");
        if (!token) return;
        try {
            const res = await fetch(`${API}/users/me/preferences`, { headers: authHeaders() });
            if (res.ok) {
                const d = await res.json();
                if (d?.theme) setThemeName(d.theme);
            }
        } catch { } // sessizce geç
    }, []);

    useEffect(() => { fetchPreference(); }, [fetchPreference]);

    // ── Tema değiştir + backend'e kaydet ─────────────────────────────────────
    const toggleTheme = useCallback(async () => {
        const next = themeName === "dark" ? "light" : "dark";
        setThemeName(next);
        localStorage.setItem("sf_theme", next);
        setSyncing(true);
        try {
            await fetch(`${API}/users/me/preferences`, {
                method: "PATCH",
                headers: authHeaders(),
                body: JSON.stringify({ theme: next }),
            });
        } catch { }
        finally { setSyncing(false); }
    }, [themeName]);

    const setTheme = useCallback(async (name) => {
        if (name !== "dark" && name !== "light") return;
        setThemeName(name);
        localStorage.setItem("sf_theme", name);

        setSyncing(true);
        try {
            await fetch(`${API}/users/me/preferences`, {
                method: "PATCH",
                headers: authHeaders(),
                body: JSON.stringify({ theme: name }),
            });
        } catch { }
        finally { setSyncing(false); }
    }, []);

    // ── <html> data-theme attribute + scrollbar rengi ─────────────────────────
    useEffect(() => {
        document.documentElement.setAttribute("data-theme", themeName);
        document.documentElement.style.setProperty(
            "--scrollbar-track", t.scrollbar.track
        );
        document.documentElement.style.setProperty(
            "--scrollbar-thumb", t.scrollbar.thumb
        );
        document.body.style.background = t.bg.page;
    }, [themeName, t]);

    return (
        <ThemeContext.Provider value={{ t, themeName, toggleTheme, setTheme, syncing }}>
            {children}
        </ThemeContext.Provider>
    );
}

// ─── HOOK ────────────────────────────────────────────────────────────────────
export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
    return ctx;
}

// ─── THEME TOGGLE BUTTON (her yerde kullanılabilir) ──────────────────────────
export function ThemeToggleButton({
    style,
    lightLabel = "Açık Tema",
    darkLabel = "Koyu Tema",
    lightTitle = "Açık temaya geç",
    darkTitle = "Koyu temaya geç",
}) {
    const { themeName, toggleTheme, syncing, t } = useTheme();
    const isDark = themeName === "dark";

    return (
        <button
            onClick={toggleTheme}
            disabled={syncing}
            title={isDark ? lightTitle : darkTitle}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 14px",
                borderRadius: 10,
                border: `1px solid ${t.border.input}`,
                background: t.bg.toggle,
                color: t.text.secondary,
                cursor: syncing ? "default" : "pointer",
                fontSize: 13,
                fontWeight: 600,
                transition: "all 0.2s",
                opacity: syncing ? 0.6 : 1,
                ...style,
            }}
        >
            <span style={{ fontSize: 16, lineHeight: 1 }}>
                {isDark ? "☀️" : "🌙"}
            </span>
            {isDark ? lightLabel : darkLabel}
            {syncing && (
                <span style={{
                    width: 12, height: 12,
                    border: `2px solid ${t.border.default}`,
                    borderTopColor: t.accent.blue,
                    borderRadius: "50%",
                    display: "inline-block",
                    animation: "sf-spin 0.7s linear infinite",
                }} />
            )}
        </button>
    );
}
